import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '../components/layout';
import { LoadingSpinner } from '../components/common';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { usePageTracking } from '../hooks';
import { ROUTES } from '../constants';

const HomePage = lazy(() =>
  import('./HomePage').then((module) => ({ default: module.HomePage }))
);
const PlacesPage = lazy(() =>
  import('./PlacesPage').then((module) => ({ default: module.PlacesPage }))
);
const CreatePlacePage = lazy(() =>
  import('./CreatePlacePage').then((module) => ({ default: module.CreatePlacePage }))
);
const ExperiencesPage = lazy(() =>
  import('./ExperiencesPage').then((module) => ({ default: module.ExperiencesPage }))
);
const CreateExperiencePage = lazy(() =>
  import('./CreateExperiencePage').then((module) => ({
    default: module.CreateExperiencePage,
  }))
);
const MapViewPage = lazy(() =>
  import('./MapViewPage').then((module) => ({ default: module.MapViewPage }))
);
const ItemDetailsPage = lazy(() =>
  import('./ItemDetailsPage').then((module) => ({ default: module.ItemDetailsPage }))
);
const EditItemPage = lazy(() =>
  import('./EditItemPage').then((module) => ({ default: module.EditItemPage }))
);
const SettingsPage = lazy(() =>
  import('./SettingsPage').then((module) => ({ default: module.SettingsPage }))
);
const AboutPage = lazy(() =>
  import('./AboutPage').then((module) => ({ default: module.AboutPage }))
);
const FriendsPage = lazy(() =>
  import('./FriendsPage').then((module) => ({ default: module.FriendsPage }))
);
const LoginPage = lazy(() =>
  import('./LoginPage').then((module) => ({ default: module.LoginPage }))
);

/**
 * Component to enable page view tracking
 */
const PageTracker: React.FC = () => {
  usePageTracking();
  return null;
};

/**
 * Main application router configuration
 */
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <PageTracker />
      <Suspense fallback={<LoadingSpinner message="Loading..." />}>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.PLACES} element={<PlacesPage />} />
            <Route path={ROUTES.CREATE_PLACE} element={<CreatePlacePage />} />
            <Route path={ROUTES.EXPERIENCES} element={<ExperiencesPage />} />
            <Route path={ROUTES.CREATE_EXPERIENCE} element={<CreateExperiencePage />} />
            <Route path={ROUTES.MAP_VIEW} element={<MapViewPage />} />
            <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
            <Route path={ROUTES.ABOUT} element={<AboutPage />} />
            <Route path={ROUTES.FRIENDS} element={<FriendsPage />} />
            <Route path={ROUTES.ITEM_DETAILS} element={<ItemDetailsPage />} />
            <Route path={ROUTES.EDIT_ITEM} element={<EditItemPage />} />
            <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
